// -----------------------------------------------------------------------------
// <copyright file="tt.directive.ts" company="ecoInsight, Inc.">
// Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------
import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  Renderer2,
} from '@angular/core';

@Directive({
  selector: '[tooltip]',
  standalone: false,
})
export class TooltipDirective implements OnDestroy {
  tooltipTitle = input<string>('', { alias: 'tooltip' });
  placement = input<string>('bottom');
  delay = input<string>('150');
  isTooltipTitleHtml = input<boolean>(false);
  leftPadding = input<number | null>(null);
  highIndex = input<boolean>(false);
  checkOverflow = input<boolean>(false);

  private tooltip: HTMLElement | null = null;
  private offset = 10;
  private firefoxOffset = 25;
  private browserName: string;
  private unlistenWindowScroll: (() => void) | null = null;
  private unlistenWindowClick: (() => void) | null = null;

  private el = inject(ElementRef);
  private renderer = inject(Renderer2);

  constructor() {
    this.unlistenWindowClick = this.renderer.listen('window', 'click', () => {
      this.hideImmediate();
    });

    this.unlistenWindowScroll = this.renderer.listen('window', 'scroll', () => {
      if (this.tooltip) {
        this.setPosition();
      }
    });

    this.browserName = this.detectBrowserName();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.tooltip) {
      this.show();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hide();
  }

  @HostListener('click')
  onClick(): void {
    this.hide();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    if (!this.tooltip) {
      this.show();
    }
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.hide();
  }

  private detectBrowserName(): string {
    const agent = navigator.userAgent.toLowerCase();
    if (agent.includes('edge')) return 'edge';
    if (agent.includes('opr') && (window as any).opr) return 'opera';
    if (agent.includes('chrome') && (window as any).chrome) return 'chrome';
    if (agent.includes('trident')) return 'ie';
    if (agent.includes('firefox')) return 'firefox';
    if (agent.includes('safari')) return 'safari';
    return 'other';
  }

  private show(): void {
    if (!this.tooltipTitle()) return;

    if (this.checkOverflow()) {
      const host = this.el.nativeElement as HTMLElement;
      const style = window.getComputedStyle(host);
      const widthOverflow = host.scrollWidth - host.offsetWidth > 1;

      // Single-line (white-space: nowrap) truncation can only overflow
      // horizontally. Including the height comparison for these elements
      // produces false positives from font ascender/descender metrics
      // exceeding the line box by a consistent few pixels, which triggers
      // the tooltip even when the text isn't actually clipped.
      const isSingleLine = style.whiteSpace === 'nowrap';

      let overflow: boolean;
      if (isSingleLine) {
        overflow = widthOverflow;
      } else {
        // -webkit-line-clamp elements (e.g. ellipsis-two) often carry a
        // min-height that keeps empty/short rows visually consistent. That
        // min-height inflates scrollHeight along with offsetHeight by a
        // fraction of a line, so a flat 1px tolerance isn't enough to
        // absorb it. Scale the tolerance to a fraction of one line's
        // height instead — genuine additional-line overflow is always
        // much larger than this.
        const lineHeight = parseFloat(style.lineHeight);
        const heightTolerance = Number.isFinite(lineHeight) ? lineHeight * 0.3 : 2;
        const heightOverflow = host.scrollHeight - host.offsetHeight > heightTolerance;

        overflow = heightOverflow || widthOverflow;
      }

      if (!overflow) return;
    }

    this.createTooltip();
    this.setPosition();
    this.renderer.addClass(this.tooltip!, 'ng-tooltip-show');
  }

  private hide(): void {
    if (!this.tooltip) return;

    this.renderer.removeClass(this.tooltip, 'ng-tooltip-show');
    setTimeout(() => {
      this.destroyTooltip();
    }, 0);
  }

  private hideImmediate(): void {
    this.destroyTooltip();
  }

  private createTooltip(): void {
    this.tooltip = this.renderer.createElement('span');
    if (!this.tooltip) return;

    // Generate unique ID to avoid conflicts when multiple tooltips exist
    const tooltipId = 'ng-tooltip-' + Math.random().toString(36).substr(2, 9);
    this.tooltip.setAttribute('id', tooltipId);

    if (this.isTooltipTitleHtml()) {
      this.tooltip.innerHTML = this.tooltipTitle();
    } else {
      const text = this.renderer.createText(this.tooltipTitle());
      this.renderer.appendChild(this.tooltip, text);
    }

    this.renderer.appendChild(document.body, this.tooltip);

    this.renderer.addClass(this.tooltip, 'ng-tooltip');
    this.renderer.addClass(this.tooltip, `ng-tooltip-${this.placement()}`);

    if (this.highIndex()) {
      this.renderer.addClass(this.tooltip, 'ng-tooltip-high-index');
    }

    const transition = `opacity ${this.delay}ms`;
    this.renderer.setStyle(this.tooltip, 'transition', transition);
    this.renderer.setStyle(this.tooltip, '-webkit-transition', transition);
    this.renderer.setStyle(this.tooltip, '-moz-transition', transition);
    this.renderer.setStyle(this.tooltip, '-o-transition', transition);
  }

  private setPosition(): void {
    if (!this.tooltip) return;

    const hostPos = this.el.nativeElement.getBoundingClientRect();
    const tooltipPos = this.tooltip.getBoundingClientRect();

    let top = 0;
    let left = 0;

    // Using fixed positioning, so we don't need to add scrollTop
    switch (this.placement()) {
      case 'top':
        top = hostPos.top - tooltipPos.height - this.offset;
        left = hostPos.left + (hostPos.width - tooltipPos.width) / 2;
        break;

      case 'bottom':
        top = hostPos.bottom + this.offset;
        left = hostPos.left + 10;
        break;

      case 'left':
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        const currentOffset = this.browserName === 'firefox' ? this.firefoxOffset : this.offset;
        left = hostPos.left - tooltipPos.width - currentOffset + (this.leftPadding() ?? 5);
        break;

      case 'right':
        top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
        left = hostPos.right + this.offset;
        break;
    }

    // Minimum margin to keep from viewport edges
    const minMargin = 15;

    if (top > tooltipPos.top - 50 && this.placement() !== 'left') {
      // If tooltip is displaying out of the browser window, then display tooltip above the element
      top = hostPos.top - tooltipPos.height - this.offset;
      left = hostPos.left + 10;
    }

    if (hostPos.left + 15 > window.innerWidth) {
      // If tooltip is displaying right of the browser window, then display tooltip left to the element
      top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
      left = hostPos.left - tooltipPos.width - this.offset;
    }

    // Check if left-positioned tooltip would go off-screen or too close to left edge
    if (this.placement() === 'left' && left < minMargin) {
      // If tooltip is too close or off the left edge, display it to the right instead
      top = hostPos.top + (hostPos.height - tooltipPos.height) / 2;
      left = hostPos.right + this.offset;
    }

    // Ensure tooltip doesn't go off right edge
    if (left + tooltipPos.width > window.innerWidth - minMargin) {
      left = window.innerWidth - tooltipPos.width - minMargin;
    }

    // Ensure tooltip doesn't go off left edge
    if (left < minMargin) {
      left = minMargin;
    }

    this.renderer.setStyle(this.tooltip, 'top', `${top}px`);
    this.renderer.setStyle(this.tooltip, 'left', `${left}px`);
  }

  ngOnDestroy(): void {
    this.destroyTooltip();

    if (this.unlistenWindowClick) {
      try {
        this.unlistenWindowClick();
      } catch {
        //
      }
      this.unlistenWindowClick = null;
    }

    if (this.unlistenWindowScroll) {
      try {
        this.unlistenWindowScroll();
      } catch {
        //
      }
      this.unlistenWindowScroll = null;
    }

    this.tooltip = null;
  }

  private destroyTooltip(): void {
    if (!this.tooltip) return;

    try {
      this.renderer.removeChild(document.body, this.tooltip);
    } catch {
      //
    }

    this.tooltip = null;
  }
}
